import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Chip,
  Alert,
  useTheme,
  alpha,
  DialogContentText
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import axios from '../utils/axiosConfig';





function Settings() {
  const { user } = useAuth();
  const { logout } = useAuth();
  const [searchParams] = useSearchParams();
  const billingRef = useRef(null);
  const [superusers, setSuperusers] = useState([]);
  const [openSuperuserDialog, setOpenSuperuserDialog] = useState(false);
  const [newSuperuser, setNewSuperuser] = useState({
    email: '',
    name: '',
    isExistingUser: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Scroll to billing section if query param is present
  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'billing' && billingRef.current) {
      setTimeout(() => {
        billingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [searchParams]);

  useEffect(() => {
    // Load superusers from API when a superuser is logged in
    const token = localStorage.getItem('token');
    if (user?.isSuperuser && token) {
      fetchSuperusers();
    } else {
      setSuperusers([]);
    }
  }, [user]);

  const fetchSuperusers = async () => {
    try {
      console.log('Fetching superusers...');
      // Using the correct API endpoint path
      const response = await fetch(`${API_URL}/api/v1/users/superusers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token') // Adding x-auth-token as fallback
        }
      });
      
      console.log('Superusers fetch response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Superusers data:', data);
        setSuperusers(data);
      } else {
        const errorText = await response.text();
        console.error('Error response from superusers endpoint:', errorText);
      }
    } catch (error) {
      console.error('Error fetching superusers:', error);
      setError('Failed to load superusers');
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeleteLoading(true);
    try {
      await axios.delete('/api/v1/auth/me');
      logout();
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to delete account', err);
      setError('Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
    }
  };

  // Removed unused checkIfSuperuser function

  const handleAddSuperuser = async () => {
    try {
      // Validate input
      if (!newSuperuser.email) {
        setError('Email is required');
        return;
      }
      
      if (!newSuperuser.isExistingUser && !newSuperuser.name) {
        setError('Name is required for new users');
        return;
      }
      
      console.log('Adding superuser:', newSuperuser);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/v1/users/superusers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token // Adding x-auth-token as fallback
        },
        body: JSON.stringify(newSuperuser)
      });

      console.log('Add superuser response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('New superuser data:', data);
        setSuperusers([...superusers, data]);
        setOpenSuperuserDialog(false);
        setNewSuperuser({ email: '', name: '', isExistingUser: true });
        
        if (newSuperuser.isExistingUser) {
          setSuccess(`Existing user ${newSuperuser.email} has been granted superuser privileges`);
        } else {
          setSuccess(`New superuser account created for ${newSuperuser.name}`);
        }
        
        fetchSuperusers();
      } else {
        const errorText = await response.text();
        console.error('Error response from add superuser endpoint:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.msg || 'Failed to add superuser');
        } catch (e) {
          setError('Failed to add superuser: Server error');
        }
      }
    } catch (error) {
      console.error('Error adding superuser:', error);
      setError('Failed to add superuser: ' + (error.message || 'Unknown error'));
    }
  };

  const handleUpdateSuperuser = async (superuserId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/users/superusers/${superuserId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        body: JSON.stringify({ active: status })
      });
      if (!response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          throw new Error(data.msg || 'Failed to update superuser');
        } catch (e) {
          throw new Error('Failed to update superuser');
        }
      }
      const updated = await response.json();
      setSuperusers(prev => prev.map(s => s._id === updated._id ? updated : s));
      setSuccess('Superuser status updated successfully');
    } catch (error) {
      console.error('Error updating superuser status:', error);
      setError('Failed to update superuser status');
    }
  };

  const handleRemoveSuperuser = async (superuserId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/users/superusers/${superuserId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        }
      });
      if (!response.ok) {
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          throw new Error(data.msg || 'Failed to remove superuser');
        } catch (e) {
          throw new Error('Failed to remove superuser');
        }
      }
      setSuperusers(prev => prev.filter(s => s._id !== superuserId));
      setSuccess('Superuser removed successfully');
    } catch (error) {
      console.error('Error removing superuser:', error);
      setError('Failed to remove superuser');
    }
  };

  // Gate access by superuser
  const isSuperuser = !!user?.isSuperuser;
  const PROTECTED_SUPERUSER_EMAIL = 'nathan.mcconnell@sandboxentmt.com';
  const theme = useTheme();
  
  return (
    <Container maxWidth="lg" sx={{ py: 2.5 }}>
      {/* Premium Header */}
      <Paper 
        elevation={0} 
        sx={{
          mb: 2.5, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: theme.palette.mode === 'dark' 
            ? `linear-gradient(to right bottom, ${alpha(theme.palette.primary.dark, 0.15)}, ${alpha(theme.palette.background.paper, 0.9)})` 
            : `linear-gradient(to right bottom, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(theme.palette.background.paper, 0.9)})`,
          backdropFilter: 'blur(10px)',
          p: 2,
          borderRadius: 2,
          boxShadow: theme.palette.mode === 'dark'
            ? `0 4px 20px ${alpha(theme.palette.common.black, 0.2)}`
            : '0 4px 20px rgba(0,0,0,0.05)',
          border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.1)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #2196F3, #1976D2)',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            opacity: 0.8
          }
        }}
      >
        <Box>
          <Typography variant="h5" sx={{
            fontWeight: 600,
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: theme.palette.mode === 'dark' ? 
              alpha(theme.palette.common.white, 0.95) : 
              'text.primary',
            textShadow: theme.palette.mode === 'dark' ? 
              '0 0 1px rgba(255,255,255,0.1)' : 
              'none'
          }}>
            <SettingsIcon sx={{ 
              color: theme.palette.mode === 'dark' ? 
                '#90CAF9' : 
                'primary.main',
              filter: theme.palette.mode === 'dark' ? 
                'drop-shadow(0 0 2px rgba(33, 150, 243, 0.5))' : 
                'none'
            }} />
            Settings
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              maxWidth: 500,
              color: theme.palette.mode === 'dark' ? 
                alpha(theme.palette.common.white, 0.7) : 
                'text.secondary'
            }}
          >
            Manage system settings and superuser access
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ 
        p: 4, 
        borderRadius: 3,
        backgroundColor: theme.palette.mode === 'dark' ? 
          alpha('#1a1a1a', 0.9) : 
          theme.palette.background.paper,
        backdropFilter: 'blur(10px)',
        border: theme.palette.mode === 'dark' ? 
          `1px solid ${alpha(theme.palette.divider, 0.25)}` : 
          '1px solid rgba(0,0,0,0.03)',
        boxShadow: theme.palette.mode === 'dark' ? 
          `0 8px 24px ${alpha(theme.palette.common.black, 0.6)}, 0 0 0 1px ${alpha(theme.palette.common.white, 0.05)}` : 
          '0 4px 12px rgba(0,0,0,0.08)'
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {!isSuperuser && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You do not have superuser privileges. Only designated superusers can access this page.
          </Alert>
        )}

        {/* Payment & Billing Section - Placeholder */}
        <Box ref={billingRef} sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`
          }}>
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0.5
              }}>
                <PaymentIcon sx={{ color: 'primary.main' }} />
                Payment & Billing
              </Typography>
              <Typography variant="body2" sx={{ 
                color: theme.palette.mode === 'dark' ? 
                  alpha(theme.palette.common.white, 0.7) : 
                  'text.secondary'
              }}>
                Manage your subscription and payment methods
              </Typography>
            </Box>
          </Box>

          {/* Payment Cards Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 2,
            mb: 3
          }}>
            {/* Current Plan Card */}
            <Paper sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 
                alpha(theme.palette.background.paper, 0.4) : 
                alpha(theme.palette.background.paper, 0.5),
              border: theme.palette.mode === 'dark'
                ? `1px solid ${alpha(theme.palette.divider, 0.15)}`
                : `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.3),
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.05)
                  : alpha(theme.palette.primary.main, 0.02)
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  p: 1,
                  borderRadius: 2,
                  mr: 2
                }}>
                  <CreditCardIcon sx={{ color: 'primary.main' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Current Plan
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Free Tier
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No payment required
              </Typography>
              <Chip 
                label="Free Access" 
                color="success" 
                size="small"
                sx={{ 
                  fontWeight: 600,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.success.main, 0.2)
                    : alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                }}
              />
            </Paper>

            {/* Payment Method Card */}
            <Paper sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 
                alpha(theme.palette.background.paper, 0.4) : 
                alpha(theme.palette.background.paper, 0.5),
              border: theme.palette.mode === 'dark'
                ? `1px solid ${alpha(theme.palette.divider, 0.15)}`
                : `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.3),
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.05)
                  : alpha(theme.palette.primary.main, 0.02)
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  p: 1,
                  borderRadius: 2,
                  mr: 2
                }}>
                  <LockIcon sx={{ color: 'primary.main' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Payment Method
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                No payment method required
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Currently free to use
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                disabled
                sx={{ 
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Add Payment Method
              </Button>
            </Paper>
          </Box>

          {/* Coming Soon Alert */}
          <Alert 
            severity="info" 
            icon={<PaymentIcon />}
            sx={{
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.1)
                : alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
              '& .MuiAlert-icon': {
                color: 'info.main'
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              <strong>Currently free to use!</strong> Payment and subscription features will be introduced in a future release. Enjoy full access to all features at no cost.
            </Typography>
          </Alert>
        </Box>

        {/* Superuser Management Section - Only visible to superusers */}
        {isSuperuser && (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 3,
              pb: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`
            }}>
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5
                }}>
                  <SupervisorAccountIcon sx={{ color: 'primary.main' }} />
                  Superuser Management
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.mode === 'dark' ? 
                    alpha(theme.palette.common.white, 0.7) : 
                    'text.secondary'
                }}>
                  Superusers have access to all projects and can approve or deny resources
                </Typography>
              </Box>
              <Button
                startIcon={<PersonAddIcon />}
                variant="contained"
                onClick={() => setOpenSuperuserDialog(true)}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                    : '0 2px 8px rgba(33, 150, 243, 0.2)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                      : '0 4px 12px rgba(33, 150, 243, 0.3)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Add Superuser
              </Button>
            </Box>

          <List sx={{ 
            '& .MuiListItem-root': {
              borderRadius: 2,
              mb: 1,
              border: theme.palette.mode === 'dark'
                ? `1px solid ${alpha(theme.palette.divider, 0.15)}`
                : `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.4)
                : alpha(theme.palette.background.paper, 0.5),
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.08)
                  : alpha(theme.palette.primary.main, 0.04),
                borderColor: alpha(theme.palette.primary.main, 0.3)
              }
            }
          }}>
            {superusers.map((superuser) => (
              <ListItem key={superuser._id} sx={{ py: 2 }}>
                <ListItemText
                  primary={superuser.name}
                  secondary={superuser.email}
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: 600,
                      fontSize: '0.95rem'
                    }
                  }}
                  secondaryTypographyProps={{
                    sx: {
                      color: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.6)
                        : 'text.secondary',
                      fontSize: '0.85rem'
                    }
                  }}
                />
                <Chip
                  label={superuser.active ? 'Active' : 'Inactive'}
                  color={superuser.active ? 'success' : 'default'}
                  size="small"
                  sx={{ 
                    mr: 1,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    ...(superuser.active && {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.success.main, 0.2)
                        : alpha(theme.palette.success.main, 0.1),
                      color: theme.palette.success.main,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                    })
                  }}
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={superuser.active}
                    onChange={() => handleUpdateSuperuser(superuser._id, !superuser.active)}
                    disabled={superuser.email === PROTECTED_SUPERUSER_EMAIL && user?.email !== PROTECTED_SUPERUSER_EMAIL}
                    sx={{ mr: 1 }}
                  />
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveSuperuser(superuser._id)}
                    disabled={superuser.email === PROTECTED_SUPERUSER_EMAIL && user?.email !== PROTECTED_SUPERUSER_EMAIL}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
        )}

        {/* Add Superuser Dialog */}
        <Dialog 
          open={openSuperuserDialog} 
          onClose={() => setOpenSuperuserDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              backgroundColor: theme.palette.mode === 'dark' ? 
                alpha('#1a1a1a', 0.95) : 
                theme.palette.background.paper,
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark' ? 
                `1px solid ${alpha(theme.palette.divider, 0.25)}` : 
                'none',
              boxShadow: theme.palette.mode === 'dark' ? 
                `0 12px 48px ${alpha(theme.palette.common.black, 0.7)}` : 
                '0 8px 32px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 600,
            borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
            pb: 2
          }}>
            Add Superuser
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>User Type</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant={newSuperuser.isExistingUser ? "contained" : "outlined"}
                  onClick={() => setNewSuperuser({ ...newSuperuser, isExistingUser: true })}
                  sx={{ flexGrow: 1 }}
                >
                  Existing User
                </Button>
                <Button 
                  variant={!newSuperuser.isExistingUser ? "contained" : "outlined"}
                  onClick={() => setNewSuperuser({ ...newSuperuser, isExistingUser: false })}
                  sx={{ flexGrow: 1 }}
                >
                  New User
                </Button>
              </Box>
            </Box>
            
            <TextField
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              value={newSuperuser.email}
              onChange={(e) => setNewSuperuser({ ...newSuperuser, email: e.target.value })}
              required
              sx={{ mb: 2 }}
            />
            
            {!newSuperuser.isExistingUser && (
              <TextField
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                value={newSuperuser.name}
                onChange={(e) => setNewSuperuser({ ...newSuperuser, name: e.target.value })}
                required
              />
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {newSuperuser.isExistingUser 
                ? "The existing user will be granted superuser privileges."
                : "A new user account will be created with superuser privileges. A temporary password will be generated."}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSuperuserDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddSuperuser} 
              variant="contained"
              disabled={!newSuperuser.email || (!newSuperuser.isExistingUser && !newSuperuser.name)}
            >
              Add Superuser
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>

      <Paper sx={{ p: 2, mt: 2, borderRadius: 2, border: theme.palette.mode === 'dark' ? `1px solid ${alpha(theme.palette.divider, 0.15)}` : `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DeleteIcon color="error" />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Delete account</Typography>
            <Typography variant="body2" color="text.secondary">This will permanently remove your account and data. This action cannot be undone.</Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Delete my account
        </Button>
      </Paper>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm account deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Type DELETE MY ACCOUNT to confirm. This will permanently remove your account and sign you out immediately.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Type DELETE MY ACCOUNT"
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={deleteConfirmText !== 'DELETE MY ACCOUNT' || deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Settings;
